import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import {
  LongitudinalProfile,
  NativeLifeMilestone,
  PredictiveEventType,
  PredictionOutcomeRecord,
  RemedyAdherenceRecord,
  V519PresentationMode
} from '../../types/v519';
import {
  createInitialLongitudinalProfile,
  addLifeMilestone,
  updateLifeMilestone,
  removeLifeMilestone,
  addPredictionOutcomeRecord,
  recordRemedyFeedback,
  markRemedyCompleted
} from '../../utils/v519/longitudinalIntelligenceEngine';
import { EVENT_RULE_REGISTRY } from '../../utils/v519/eventPredictionEngine';
import { formatDate } from '../../utils/dateUtils';
import {
  History,
  Plus,
  Trash2,
  Edit3,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  Award,
  BookOpen,
  Calendar,
  Check,
  X,
  TrendingUp,
  Activity,
  HeartHandshake,
  MessageSquare,
  ShieldAlert,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export interface LifeMilestoneCalibrationCardProps {
  kundali: CompleteKundali;
  longitudinalProfile?: LongitudinalProfile;
  onUpdateProfile?: (updatedProfile: LongitudinalProfile) => void;
  lang: Language;
  presentationMode?: V519PresentationMode;
}

export const LifeMilestoneCalibrationCard: React.FC<LifeMilestoneCalibrationCardProps> = ({
  kundali,
  longitudinalProfile,
  onUpdateProfile,
  lang,
  presentationMode = 'PRO'
}) => {
  const isHi = lang === 'hi';
  const isBasic = presentationMode === 'BASIC';
  const isLearner = (presentationMode as string) === 'LEARNER';
  const isPro = presentationMode === 'PRO';

  // Internal Profile State if none provided from parent
  const [internalProfile, setInternalProfile] = useState<LongitudinalProfile>(() => {
    if (longitudinalProfile) return longitudinalProfile;
    // Default initial profile
    const base = createInitialLongitudinalProfile(kundali.birthDetails?.name || 'Native-1');
    // Pre-seed with one sample milestone for calibration demonstration if empty
    return addLifeMilestone(
      base,
      {
        eventType: 'CAREER_PROMOTION',
        eventTitle: 'First Significant Career Elevation',
        date: '2023-04-15',
        notes: 'Promoted to Senior Lead during Guru Antardasha',
        source: 'CLIENT_INTERVIEW',
        verified: true
      },
      kundali
    );
  });

  const profile = longitudinalProfile || internalProfile;

  const handleUpdate = (updated: LongitudinalProfile) => {
    setInternalProfile(updated);
    if (onUpdateProfile) {
      onUpdateProfile(updated);
    }
  };

  // Sub-tab navigation
  const [activeSubTab, setActiveSubTab] = useState<'milestones' | 'outcomes' | 'remedies' | 'sessions'>('milestones');

  // Form State for Adding New Milestone
  const [isAddingMilestone, setIsAddingMilestone] = useState<boolean>(false);
  const [newEventType, setNewEventType] = useState<PredictiveEventType>('CAREER_PROMOTION');
  const [newEventTitle, setNewEventTitle] = useState<string>('');
  const [newEventDate, setNewEventDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [newEventNotes, setNewEventNotes] = useState<string>('');
  const [newEventVerified, setNewEventVerified] = useState<boolean>(true);
  const [formError, setFormError] = useState<string>('');

  const handleAddMilestoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!newEventDate || isNaN(new Date(newEventDate).getTime())) {
      setFormError(isHi ? 'कृपया मान्य तिथि (YYYY-MM-DD) दर्ज करें।' : 'Please enter a valid date (YYYY-MM-DD).');
      return;
    }

    try {
      const updated = addLifeMilestone(
        profile,
        {
          eventType: newEventType,
          eventTitle: newEventTitle.trim() || undefined,
          date: newEventDate,
          notes: newEventNotes.trim(),
          source: 'USER_SUBMISSION',
          verified: newEventVerified
        },
        kundali
      );
      handleUpdate(updated);
      setIsAddingMilestone(false);
      setNewEventNotes('');
      setNewEventTitle('');
    } catch (err: any) {
      setFormError(err.message || 'Failed to calibrate milestone.');
    }
  };

  const handleRemoveMilestone = (id: string) => {
    try {
      const updated = removeLifeMilestone(profile, id, kundali);
      handleUpdate(updated);
    } catch (err: any) {
      console.error('Failed removing milestone:', err);
    }
  };

  const summary = profile.calibrationSummary;
  const isCalibrationStrong = summary.averageCalibrationScore >= 70;
  const isCalibrationWeak = summary.averageCalibrationScore < 50;

  return (
    <section
      id="life-milestone-calibration-card"
      aria-labelledby="milestone-calibration-heading"
      className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-xs space-y-6 text-astro-text"
    >
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div className="flex items-start sm:items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-astro-primary text-astro-accent flex items-center justify-center shrink-0 shadow-xs">
            <History className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 id="milestone-calibration-heading" className="text-base sm:text-lg font-bold text-astro-primary font-serif">
                {isHi ? 'दीर्घकालिक जीवन मील का पत्थर एवं समय अंशांकन' : 'Longitudinal Life Milestone & Timeline Calibration'}
              </h3>
              <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
                Empirical Feedback
              </span>
            </div>
            <p className="text-xs text-astro-secondary-text mt-0.5">
              {isHi
                ? 'वास्तविक जीवन की घटनाओं से विंशोत्तरी दशा व ग्रहों की सक्रियता का शास्त्रीय अंशांकन'
                : 'Calibrates natal chart timing against verified historical life events to measure predictive accuracy'}
            </p>
          </div>
        </div>

        {/* Global Calibration Score Badge */}
        <div className="flex items-center gap-2 self-start md:self-center">
          <div className="text-right">
            <div className="text-[11px] text-astro-secondary-text">{isHi ? 'औसत अंशांकन सटीकता' : 'Avg Calibration Accuracy'}</div>
            <div className="text-xs font-bold text-astro-primary">
              {summary.verifiedMilestones}/{summary.totalMilestones} {isHi ? 'सत्यापित घटनाएं' : 'Verified Events'}
            </div>
          </div>
          <div
            className={`px-3 py-1.5 rounded-xl text-base font-extrabold shadow-xs ${
              isCalibrationStrong
                ? 'bg-emerald-700 text-white'
                : isCalibrationWeak
                ? 'bg-amber-600 text-white'
                : 'bg-teal-700 text-white'
            }`}
          >
            {summary.averageCalibrationScore}%
          </div>
        </div>
      </div>

      {/* Sub-tab Switcher */}
      <div className="flex items-center gap-2 border-b border-astro-border pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveSubTab('milestones')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'milestones'
              ? 'bg-astro-primary text-white shadow-2xs'
              : 'text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <Calendar className="w-3.5 h-3.5" />
          <span>{isHi ? 'जीवन मील के पत्थर' : 'Life Milestones'} ({profile.milestones.length})</span>
        </button>

        <button
          onClick={() => setActiveSubTab('outcomes')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'outcomes'
              ? 'bg-astro-primary text-white shadow-2xs'
              : 'text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          <span>{isHi ? 'पूर्वानुमान सत्यापन (Hit Rate)' : 'Prediction Outcomes'} ({profile.predictionOutcomes?.length || 0})</span>
        </button>

        <button
          onClick={() => setActiveSubTab('remedies')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'remedies'
              ? 'bg-astro-primary text-white shadow-2xs'
              : 'text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          <span>{isHi ? 'उपाय पालन व प्रभाव' : 'Remedy Adherence'} ({profile.remedyAdherenceLog.length})</span>
        </button>

        <button
          onClick={() => setActiveSubTab('sessions')}
          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
            activeSubTab === 'sessions'
              ? 'bg-astro-primary text-white shadow-2xs'
              : 'text-astro-secondary-text hover:bg-astro-card'
          }`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span>{isHi ? 'परामर्श सत्र इतिहास' : 'Consultation History'} ({profile.consultationSessions.length})</span>
        </button>
      </div>

      {/* SUB-TAB 1: MILESTONES & CALIBRATION */}
      {activeSubTab === 'milestones' && (
        <div className="space-y-5">
          {/* Calibration Summary Insights */}
          <div className="p-4 bg-astro-card border border-astro-border rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'अंशांकन निष्कर्ष एवं अंतर्दृष्टि' : 'Historical Calibration Insights'}</span>
              </h4>
              <span className="text-[11px] text-astro-secondary-text">
                {isHi ? `अंतिम अंशांकन: ${formatDate(summary.lastCalibratedAt)}` : `Last Calibrated: ${formatDate(summary.lastCalibratedAt)}`}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              {/* Strong Validated Patterns */}
              <div className="p-3 bg-white rounded-lg border border-emerald-200 space-y-1">
                <span className="font-bold text-emerald-900 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  {isHi ? 'पुष्ट शास्त्रीय संकेत' : 'Strongly Validated Patterns'}
                </span>
                <ul className="space-y-1 text-astro-text text-[11px]">
                  {(isHi ? summary.strongestValidatedPatternsHi : summary.strongestValidatedPatternsEn).map((pat, idx) => (
                    <li key={idx} className="flex items-start gap-1.5">
                      <span className="text-emerald-600 font-bold">•</span>
                      <span>{pat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weak Patterns / Discrepancies */}
              <div className="p-3 bg-white rounded-lg border border-amber-200 space-y-1">
                <span className="font-bold text-amber-900 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                  {isHi ? 'ध्यान देने योग्य विसंगतियां' : 'Discrepancies / Anomalies'}
                </span>
                <ul className="space-y-1 text-astro-text text-[11px]">
                  {(isHi ? summary.weakPatternsHi : summary.weakPatternsEn).map((pat, idx) => (
                    <li key={idx} className="flex items-start gap-1.5">
                      <span className="text-amber-600 font-bold">•</span>
                      <span>{pat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Birth Time Rectification Recommendation if Score < 50 */}
            {isCalibrationWeak && (
              <div className="p-3 bg-amber-100/70 border border-amber-300 rounded-lg text-xs text-amber-950 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-800 shrink-0 mt-0.5" />
                <p>
                  {isHi
                    ? 'चेतावनी: ऐतिहासिक अंशांकन स्कोर ५०% से कम है। यह जन्म समय में सूक्ष्म अशुद्धि का संकेत हो सकता है। कृपया "जन्म समय शोधन (Birth Time Rectification)" मॉड्यूल का उपयोग करें।'
                    : 'Notice: Historical calibration score is below 50%. This may indicate slight birth-time inaccuracies. We recommend running Birth Time Rectification.'}
                </p>
              </div>
            )}
          </div>

          {/* Action Bar: Add Milestone Button */}
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'पंजीकृत जीवन घटनाएं' : 'Registered Milestone Timeline'}</span>
            </h4>
            <button
              onClick={() => setIsAddingMilestone(!isAddingMilestone)}
              className="px-3 py-1.5 rounded-xl bg-astro-primary hover:bg-[#123D3C] text-white text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all cursor-pointer"
            >
              {isAddingMilestone ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              <span>{isAddingMilestone ? (isHi ? 'रद्द करें' : 'Cancel') : (isHi ? 'नई घटना जोड़ें' : 'Log New Milestone')}</span>
            </button>
          </div>

          {/* Add Milestone Inline Form */}
          {isAddingMilestone && (
            <form onSubmit={handleAddMilestoneSubmit} className="p-4 bg-astro-card border border-astro-border rounded-xl space-y-3 animate-fadeIn text-xs">
              <h5 className="font-bold text-astro-primary">
                {isHi ? 'नई जीवन घटना का विवरण दर्ज करें' : 'Record Life Event for Astrological Calibration'}
              </h5>

              {formError && (
                <div className="p-2.5 rounded-lg bg-rose-100 text-rose-900 font-medium">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-semibold text-astro-secondary-text mb-1">
                    {isHi ? 'घटना का प्रकार' : 'Event Type'}
                  </label>
                  <select
                    value={newEventType}
                    onChange={(e) => setNewEventType(e.target.value as PredictiveEventType)}
                    className="w-full p-2 bg-white border border-astro-border rounded-lg text-astro-text font-medium"
                  >
                    {Object.values(EVENT_RULE_REGISTRY).map((rule) => (
                      <option key={rule.eventType} value={rule.eventType}>
                        {isHi ? rule.titleHi : rule.titleEn}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-semibold text-astro-secondary-text mb-1">
                    {isHi ? 'घटना की तिथि (Date)' : 'Event Date'}
                  </label>
                  <input
                    type="date"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                    required
                    className="w-full p-2 bg-white border border-astro-border rounded-lg text-astro-text"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-astro-secondary-text mb-1">
                    {isHi ? 'शीर्षक / संदर्भ (वैकल्पिक)' : 'Title / Context (Optional)'}
                  </label>
                  <input
                    type="text"
                    placeholder={isHi ? 'उदा. प्रथम नौकरी या पदोन्नति' : 'e.g. Relocated to London'}
                    value={newEventTitle}
                    onChange={(e) => setNewEventTitle(e.target.value)}
                    className="w-full p-2 bg-white border border-astro-border rounded-lg text-astro-text"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-astro-secondary-text mb-1">
                  {isHi ? 'विस्तृत विवरण / टिप्पणी' : 'Detailed Notes & Circumstances'}
                </label>
                <textarea
                  rows={2}
                  placeholder={isHi ? 'घटना की परिस्थितियां, परिणाम आदि...' : 'Describe what happened...'}
                  value={newEventNotes}
                  onChange={(e) => setNewEventNotes(e.target.value)}
                  className="w-full p-2 bg-white border border-astro-border rounded-lg text-astro-text"
                />
              </div>

              <div className="flex items-center justify-between pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newEventVerified}
                    onChange={(e) => setNewEventVerified(e.target.checked)}
                    className="rounded text-astro-primary"
                  />
                  <span className="text-astro-secondary-text font-medium">
                    {isHi ? 'सत्यापित घटना (प्रमाणित तिथि)' : 'Verified accurate event date'}
                  </span>
                </label>

                <button
                  type="submit"
                  className="px-4 py-2 rounded-xl bg-astro-primary hover:bg-[#123D3C] text-white font-bold shadow-xs cursor-pointer"
                >
                  {isHi ? 'सहेजें एवं अंशांकन करें' : 'Save & Calibrate Dasha'}
                </button>
              </div>
            </form>
          )}

          {/* Chronological Timeline List */}
          <div className="space-y-3">
            {profile.milestones.length === 0 ? (
              <div className="p-6 bg-white rounded-xl border border-astro-border text-center text-astro-secondary-text text-xs">
                {isHi
                  ? 'अभी तक कोई मील का पत्थर पंजीकृत नहीं है। अपनी कुंडली की सटीकता मापने के लिए नई घटना जोड़ें।'
                  : 'No milestones logged yet. Add life events to calibrate your birth chart against actual history.'}
              </div>
            ) : (
              profile.milestones.map((m) => {
                const rule = EVENT_RULE_REGISTRY[m.eventType] || EVENT_RULE_REGISTRY['CAREER_PROMOTION'];
                const isAccurate = m.calibrationAccuracyScore >= 70;

                return (
                  <div
                    key={m.id}
                    className="p-3.5 sm:p-4 bg-white border border-astro-border rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-astro-accent transition-all"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm text-astro-primary">
                          {m.eventTitle || (isHi ? rule.titleHi : rule.titleEn)}
                        </span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-astro-card text-astro-secondary-text border border-astro-border">
                          {formatDate(m.date)}
                        </span>
                        {m.verified && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                            <Check className="w-2.5 h-2.5" />
                            {isHi ? 'सत्यापित' : 'Verified'}
                          </span>
                        )}
                      </div>

                      <div className="text-xs text-astro-secondary-text flex items-center gap-2 flex-wrap">
                        <span>
                          {isHi ? 'घटना के समय दशा:' : 'Dasha at event:'}{' '}
                          <strong className="text-astro-primary">
                            {m.dashaAtEvent.mahadasha}–{m.dashaAtEvent.antardasha}
                          </strong>
                        </span>
                        {m.notes && (
                          <>
                            <span>•</span>
                            <span className="italic text-astro-text">"{m.notes}"</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end sm:self-center">
                      <div className="text-right">
                        <div className="text-[10px] text-astro-secondary-text uppercase font-semibold">
                          {isHi ? 'सटीकता' : 'Accuracy'}
                        </div>
                        <div
                          className={`text-xs font-bold ${
                            isAccurate ? 'text-emerald-700' : 'text-amber-700'
                          }`}
                        >
                          {m.calibrationAccuracyScore}%
                        </div>
                      </div>

                      <button
                        onClick={() => handleRemoveMilestone(m.id)}
                        className="p-2 rounded-lg text-astro-muted hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                        title={isHi ? 'घटना हटाएं' : 'Remove milestone'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 2: PREDICTION OUTCOMES TRACKER */}
      {activeSubTab === 'outcomes' && (
        <div className="space-y-4">
          <div className="p-4 bg-astro-card border border-astro-border rounded-xl flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'पूर्वानुमान शुद्धता दर (Predictive Hit Rate)' : 'Predictive Accuracy Tracking'}
              </h4>
              <p className="text-xs text-astro-secondary-text mt-0.5">
                {isHi
                  ? 'अतीत में दिए गए ज्योतिषीय पूर्वानुमानों के वास्तविक परिणामों का निष्पक्ष रिकॉर्ड'
                  : 'Empirical record of forecasted timing windows versus actual observed outcomes'}
              </p>
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-astro-primary text-white font-extrabold text-sm">
              {profile.predictionOutcomes?.length || 0} {isHi ? 'ट्रैक किए गए' : 'Tracked'}
            </div>
          </div>

          <div className="space-y-2 text-xs">
            {(!profile.predictionOutcomes || profile.predictionOutcomes.length === 0) ? (
              <div className="p-6 bg-white rounded-xl border border-astro-border text-center text-astro-secondary-text">
                {isHi
                  ? 'वर्तमान में कोई भविष्यवाणियां परिणाम हेतु लंबित नहीं हैं। नई घटनाओं के फलित होते ही उन्हें यहां ट्रैक करें।'
                  : 'No past predictions logged for retrospective outcome tracking yet.'}
              </div>
            ) : (
              profile.predictionOutcomes.map((out) => (
                <div key={out.predictionId} className="p-3 bg-white rounded-lg border border-astro-border flex items-center justify-between">
                  <div>
                    <span className="font-bold text-astro-primary">{out.eventType}</span>
                    <div className="text-astro-secondary-text text-[11px]">
                      {formatDate(out.predictedStartDate)} to {formatDate(out.predictedEndDate)}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800">
                    {out.outcomeStatus}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: REMEDY ADHERENCE & EFFICACY */}
      {activeSubTab === 'remedies' && (
        <div className="space-y-4">
          <div className="p-4 bg-astro-card border border-astro-border rounded-xl flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'उपाय अनुपालन एवं प्रभावशीलता लॉग' : 'Remedy Adherence & Efficacy Log'}
              </h4>
              <p className="text-xs text-astro-secondary-text mt-0.5">
                {isHi
                  ? 'रत्न, मंत्र एवं दान के प्रभाव का व्यक्तिगत फीडबैक व शांति मूल्यांकन'
                  : 'Track prescribed Vedic remedies, gemstone wearing dates, and reported relief efficacy'}
              </p>
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-astro-primary text-white font-extrabold text-sm">
              {profile.remedyAdherenceLog.length} {isHi ? 'उपाय' : 'Active'}
            </div>
          </div>

          <div className="space-y-2 text-xs">
            {profile.remedyAdherenceLog.length === 0 ? (
              <div className="p-6 bg-white rounded-xl border border-astro-border text-center text-astro-secondary-text">
                {isHi
                  ? 'कोई सक्रिय उपाय लॉग नहीं किया गया है। "रत्न व वैदिक उपाय" अनुभाग से उपाय प्रारंभ करें।'
                  : 'No active remedy adherence logs found.'}
              </div>
            ) : (
              profile.remedyAdherenceLog.map((r) => (
                <div key={r.remedyId} className="p-3 bg-white rounded-lg border border-astro-border flex items-center justify-between">
                  <div>
                    <span className="font-bold text-astro-primary">{isHi ? r.remedyNameHi : r.remedyNameEn}</span>
                    <div className="text-astro-secondary-text text-[11px]">
                      {isHi ? `प्रारंभ: ${formatDate(r.startDate)}` : `Started: ${formatDate(r.startDate)}`}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800">
                    {r.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 4: CONSULTATION SESSIONS */}
      {activeSubTab === 'sessions' && (
        <div className="space-y-4">
          <div className="p-4 bg-astro-card border border-astro-border rounded-xl flex items-center justify-between">
            <div>
              <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                {isHi ? 'परामर्श सत्र निरंतरता' : 'Consultation Session Continuity'}
              </h4>
              <p className="text-xs text-astro-secondary-text mt-0.5">
                {isHi
                  ? 'एआई ज्योतिषाचार्य एवं विद्वान आचार्यों के साथ पिछले परामर्श सत्रों का सुरक्षित इतिहास'
                  : 'Past consultation memories, chief questions asked, and follow-up recommendations'}
              </p>
            </div>
          </div>

          <div className="space-y-2 text-xs">
            {profile.consultationSessions.length === 0 ? (
              <div className="p-6 bg-white rounded-xl border border-astro-border text-center text-astro-secondary-text">
                {isHi
                  ? 'कोई पिछला परामर्श सत्र उपलब्ध नहीं है।'
                  : 'No past consultation sessions recorded yet.'}
              </div>
            ) : (
              profile.consultationSessions.map((s) => (
                <div key={s.sessionId} className="p-3.5 bg-white rounded-xl border border-astro-border space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-astro-primary">{s.chiefQuestion}</span>
                    <span className="text-[11px] text-astro-secondary-text">{formatDate(s.date)}</span>
                  </div>
                  {s.outcomeNotes && <p className="text-astro-secondary-text italic">"{s.outcomeNotes}"</p>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </section>
  );
};
